
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import {
    BarChart3,
    TrendingUp,
    Users,
    IndianRupee,
    PieChart
} from 'lucide-react';
import {
    HRAnalyticsDashboard,
    AttritionAnalysis,
    SalaryTrends
} from './analytics-reporting';
import RecruitmentAnalytics from './recruitment/RecruitmentAnalytics';
import Drawer from '../../components/common/Drawer'; // Assuming generic Drawer exists

const HRAnalyticsModule = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();

    // State for tabs
    const currentTab = searchParams.get('tab') || 'hrAnalyticsDashboard';

    // State for Drawer (Drilldown/Schedule)
    const action = searchParams.get('action');
    const actionId = searchParams.get('id');
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    // Sync Drawer state with URL action
    useEffect(() => {
        if (action === 'viewDrilldown' || action === 'scheduleReport') {
            setIsDrawerOpen(true);
        } else {
            setIsDrawerOpen(false);
        }
    }, [action]);

    // Handle Tab Change
    const handleTabChange = (tabKey) => {
        const newParams = new URLSearchParams(searchParams);
        newParams.set('tab', tabKey);
        // Clear action when switching tabs? usually yes.
        newParams.delete('action');
        newParams.delete('id');
        setSearchParams(newParams);
    };

    // Close Drawer
    const closeDrawer = () => {
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('action');
        newParams.delete('id'); // Clean up ID as well
        setSearchParams(newParams);
        setIsDrawerOpen(false);
    };

    // Tabs Configuration
    const tabs = [
        {
            id: 'hrAnalyticsDashboard',
            label: 'HR Analytics Dashboard',
            icon: BarChart3,
            component: HRAnalyticsDashboard
        },
        {
            id: 'attritionAnalysis',
            label: 'Attrition Analysis',
            icon: TrendingUp,
            component: AttritionAnalysis
        },
        {
            id: 'recruitmentFunnel',
            label: 'Recruitment Funnel',
            icon: Users, // or Filter
            component: RecruitmentAnalytics
        },
        {
            id: 'salaryTrends',
            label: 'Salary Trends',
            icon: IndianRupee,
            component: SalaryTrends
        }
    ];

    // Render Component for current tab
    const renderContent = () => {
        const activeTabObj = tabs.find(t => t.id === currentTab) || tabs[0];
        const Component = activeTabObj.component;
        return <Component />;
    };

    // Determine Drawer Content based on action
    const renderDrawerContent = () => {
        if (action === 'scheduleReport') {
            return (
                <div className="p-4">
                    <h3 className="text-lg font-bold mb-4">Schedule Report</h3>
                    <p className="text-gray-500">Report scheduling functionality coming soon.</p>
                    {/* Calendar/Form would go here */}
                </div>
            );
        }

        if (action === 'viewDrilldown') {
            return (
                <div className="p-4">
                    <h3 className="text-lg font-bold mb-4">Drilldown Details</h3>
                    <p className="text-gray-500">Details for ID: {actionId}</p>
                    {/* Employee List or Cohort List component would go here */}
                </div>
            );
        }

        return null;
    };

    return (
        <div className="flex flex-col h-full bg-gray-50">
            {/* Module Header & Tabs */}
            <div className="bg-white border-b border-gray-200 px-6 pt-6 sticky top-0 z-10">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-2xl font-bold text-gray-900">HR Analytics</h1>
                    {/* Global Actions (if any) could go here, e.g. "Export All" */}
                </div>

                {/* Tab Navigation */}
                <div className="flex space-x-8 overflow-x-auto no-scrollbar">
                    {tabs.map((tab) => {
                        const isActive = currentTab === tab.id;
                        const Icon = tab.icon;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => handleTabChange(tab.id)}
                                className={`
                  group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap
                  ${isActive
                                        ? 'border-indigo-500 text-indigo-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                `}
                            >
                                <Icon className={`
                  -ml-0.5 mr-2 h-5 w-5
                  ${isActive ? 'text-indigo-500' : 'text-gray-400 group-hover:text-gray-500'}
                `} />
                                {tab.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Main Content Area */}
            <div className="flex-1 overflow-y-auto p-6">
                {renderContent()}
            </div>

            {/* Right-side Drawer */}
            <Drawer
                isOpen={isDrawerOpen}
                onClose={closeDrawer}
                title={action === 'scheduleReport' ? 'Schedule Report' : 'Drilldown View'}
                width="w-96" // or 'w-[400px]' depending on Drawer component props
            >
                {renderDrawerContent()}
            </Drawer>
        </div>
    );
};

export default HRAnalyticsModule;
